import { z } from 'zod';
import { ALLY_PERSONAS, DEFAULT_ALLY_PERSONA } from '../types/ai.js';
import { MEMORY_CATEGORIES } from '../types/memory.js';

/**
 * Zod schemas used to validate structured AI responses at runtime, both in
 * Firebase Functions (real providers) and in the client demo provider.
 * If a provider returns something that does not validate, the caller must
 * fall back to a safe plain-text reply with no memory suggestions.
 */

export const memorySuggestionSchema = z.object({
  category: z.enum(MEMORY_CATEGORIES as [string, ...string[]]),
  content: z.string().min(1).max(500),
  confidence: z.number().min(0).max(1),
  sensitive: z.boolean(),
});

export const allyTurnResponseSchema = z.object({
  reply: z.string().min(1).max(8000),
  citedFactIds: z.array(z.string()).max(50).default([]),
  memorySuggestions: z.array(memorySuggestionSchema).max(10).default([]),
  provider: z.enum(['sarvam', 'claude', 'mock']),
  refused: z.boolean().optional(),
});

export type AllyTurnResponseParsed = z.infer<typeof allyTurnResponseSchema>;

export const chartFactSchema = z.object({
  id: z.string().min(1).max(120),
  label: z.string().min(1).max(200),
  value: z.string().min(1).max(400),
  system: z.enum(['jyotish', 'western', 'meta']),
  confidence: z.enum(['exact', 'range', 'unknown']),
});

export const allyTurnRequestSchema = z.object({
  locale: z.enum(['en', 'hi', 'hinglish', 'bn']),
  timeConfidence: z.enum(['exact', 'approximate', 'unknown']),
  modelClass: z
    .enum(['standard', 'deeper', 'master'])
    .nullish()
    .transform((v) => v ?? 'standard'),
  // Which of Ally's two voices answers. Absent OR null => 'jyotish': Astrally
  // is Indian-first, so the Jyotish astrologer is the default and Western is
  // reached only by a deliberate client choice. Like recommendationContext
  // this must accept null, because the Firebase callable protocol serialises
  // an omitted field as null rather than dropping it.
  persona: z
    .enum(ALLY_PERSONAS)
    .nullish()
    .transform((v) => v ?? DEFAULT_ALLY_PERSONA),
  generationId: z
    .string()
    .max(64)
    .nullish()
    .transform((v) => v ?? undefined),
  messages: z
    .array(
      z.object({
        role: z.enum(['user', 'ally']),
        text: z.string().min(1).max(4000),
      }),
    )
    .min(1)
    .max(40),
  facts: z.array(chartFactSchema).max(200).default([]),
  memories: z
    .array(
      z.object({
        category: z.enum(MEMORY_CATEGORIES as [string, ...string[]]),
        content: z.string().min(1).max(500),
      }),
    )
    .max(60)
    .default([]),
  // The Firebase callable protocol serialises `undefined` as `null`, so this
  // must accept null as "absent" — not just undefined.
  recommendationContext: z
    .object({
      candidateDisplayName: z.string().max(80),
      grades: z.record(z.string()),
      sharedInterests: z.array(z.string()).max(20),
      rationale: z.array(z.string()).max(10),
    })
    .nullish()
    .transform((v) => v ?? undefined),
});

export type AllyTurnRequestParsed = z.infer<typeof allyTurnRequestSchema>;
